import { CSSProperties } from "react";

import { PlayyardWisps } from "./halloween/PlayyardWisps";
import { Column } from "@/components/Column";

import { IS_PLAYYARD_HALLOWEEN } from "@/lib/constants";
import { playyardHalloween } from "@/lib/playyardHalloween";
import { playyardSky } from "@/lib/playyardSky";
import { PlayyardSky } from "@/types";

const FIREFLY_COUNT = 22;
const BULBS_PER_SPAN = 5;
// String-light anchor points across the top of the yard (viewBox units).
const LIGHT_ANCHORS: [number, number][] = [
  [0, 3],
  [25, 14],
  [50, 4],
  [75, 15],
  [100, 3],
];
const LIGHT_SAG = 6;
// Halloween alternates orange and purple bulbs.
const BULB_COLORS = IS_PLAYYARD_HALLOWEEN
  ? ["#ff8a2b", "#b26bff"]
  : ["#ffe39a"];

const FIREFLIES = Array.from({ length: FIREFLY_COUNT }, (_, index) => ({
  left: 2 + playyardSky.random(index + 801) * 96,
  top: 42 + playyardSky.random(index + 901) * 54,
  durationS: 3 + playyardSky.random(index + 1001) * 4,
  delayS: -playyardSky.random(index + 1101) * 6,
  driftX: (playyardSky.random(index + 1201) - 0.5) * 8,
  driftY: (playyardSky.random(index + 1301) - 0.5) * 6,
}));

const getLightSpans = () => {
  let path = `M${LIGHT_ANCHORS[0][0].toString()} ${LIGHT_ANCHORS[0][1].toString()}`;
  const bulbs: { x: number; y: number }[] = [];
  LIGHT_ANCHORS.slice(1).forEach(([toX, toY], index) => {
    const [fromX, fromY] = LIGHT_ANCHORS[index];
    const controlX = (fromX + toX) / 2;
    const controlY = Math.max(fromY, toY) + LIGHT_SAG;
    path += ` Q${controlX.toString()} ${controlY.toString()} ${toX.toString()} ${toY.toString()}`;
    for (let step = 1; step <= BULBS_PER_SPAN; step += 1) {
      const t = step / (BULBS_PER_SPAN + 1);
      bulbs.push({
        x: (1 - t) ** 2 * fromX + 2 * (1 - t) * t * controlX + t ** 2 * toX,
        y: (1 - t) ** 2 * fromY + 2 * (1 - t) * t * controlY + t ** 2 * toY + 1,
      });
    }
  });
  return { path, bulbs };
};

const LIGHT_SPANS = getLightSpans();

// String lights that warm up as the sky darkens, plus fireflies on warm,
// dry evenings. Drawn above the night tint so they read as glowing.
export const PlayyardNightLights = ({ sky }: { sky: PlayyardSky }) => {
  /* Render */
  // Halloween trades fireflies for low ghost lights on any dry night.
  const renderGlowers = () => {
    if (IS_PLAYYARD_HALLOWEEN) {
      if (!playyardHalloween.isSpookyHour(sky.phase) || sky.isWet) return null;
      return <PlayyardWisps />;
    }
    if (!sky.hasFireflies) return null;

    return (
      <Column className="pointer-events-none absolute inset-0 z-[42]">
        {FIREFLIES.map((firefly, index) => (
          <Column
            key={index}
            className="staffer-pet-yard-firefly absolute h-[5px] w-[5px] rounded-full bg-[#fff5a8] shadow-[0_0_8px_3px_#ffe45e]"
            style={
              {
                left: `${firefly.left.toString()}%`,
                top: `${firefly.top.toString()}%`,
                animationDelay: `${firefly.delayS.toString()}s`,
                "--yard-firefly-duration": `${firefly.durationS.toString()}s`,
                "--yard-firefly-x": `${firefly.driftX.toString()}cqw`,
                "--yard-firefly-y": `${firefly.driftY.toString()}cqw`,
              } as CSSProperties
            }
          />
        ))}
      </Column>
    );
  };

  return (
    <>
      <svg
        className="pointer-events-none absolute inset-x-0 top-0 z-[42] h-[24%] w-full"
        viewBox="0 0 100 26"
        preserveAspectRatio="none"
      >
        <path
          d={LIGHT_SPANS.path}
          stroke="#2a1f1a"
          strokeWidth={0.18}
          fill="none"
          opacity={0.7}
        />
        {LIGHT_SPANS.bulbs.map((bulb, index) => (
          <circle
            key={index}
            cx={bulb.x}
            cy={bulb.y}
            r={0.55}
            fill={BULB_COLORS[index % BULB_COLORS.length]}
            className="transition-opacity duration-1000"
            style={{
              opacity: 0.35 + sky.nightAmount * 0.65,
              filter: `drop-shadow(0 0 ${(sky.nightAmount * 6).toString()}px ${BULB_COLORS[index % BULB_COLORS.length]})`,
            }}
          />
        ))}
      </svg>
      {renderGlowers()}
    </>
  );
};
